/**
 * NotificationContext
 *
 * Provides addNotification() to the entire component tree so any page
 * can trigger toast notifications without prop-drilling.
 */
import React, { createContext, useContext } from 'react';
import { useNotifications, UseNotificationsReturn } from '../hooks/useNotifications';

const NotificationContext = createContext<UseNotificationsReturn | null>(null);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useNotifications();
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Hook for components to trigger toast notifications.
 *
 * Usage:
 *   const notify = useNotify();
 *   notify.addNotification('error', 'Failed to delete report');
 *   notify.addNotification('success', 'Password updated');
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useNotify(): UseNotificationsReturn {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotify must be used within <NotificationProvider>');
  }
  return ctx;
}
